import { baseSquad, effectiveSquads, squadChain, squadId as toSquadId } from './group.js';
import { OpError, randomId, splitChangeId } from './util.js';
export const SQUAD_OPS = [
    'addSquad',
    'renameSquad',
    'describeSquad',
    'setSquadParent',
    'setSquadColor',
    'disbandSquad',
    'setSquadMember',
    'removeSquadMember',
];
export function isSquadOp(op) {
    return SQUAD_OPS.includes(op);
}
/**
 * Set one field of one squad, with the same cancel-on-return rule as people:
 * putting a name back, or a person back on the team they started on, deletes
 * the override instead of recording a second change.
 */
export function setSquadEdit(layer, squadId, mutate) {
    const squadEdits = { ...layer.squadEdits };
    const next = mutate({ ...(squadEdits[squadId] ?? {}) });
    const members = next.members ?? {};
    const cleaned = {};
    if (next.name !== undefined)
        cleaned.name = next.name;
    if (next.description !== undefined)
        cleaned.description = next.description;
    if (next.parentId !== undefined)
        cleaned.parentId = next.parentId;
    if (next.color !== undefined)
        cleaned.color = next.color;
    if (Object.keys(members).length > 0)
        cleaned.members = members;
    if (Object.keys(cleaned).length === 0)
        delete squadEdits[squadId];
    else
        squadEdits[squadId] = cleaned;
    return { ...layer, squadEdits };
}
/** The squad an operation is about, as this layer currently sees it. */
function effectiveSquad(directory, layer, squadId) {
    const squad = effectiveSquads(directory, layer).squads.find((candidate) => candidate.id === squadId);
    if (!squad)
        throw new OpError(`No team with id "${squadId}".`);
    return squad;
}
/**
 * Apply one team operation to one layer.
 *
 * `directory` is whatever sits beneath: the flat list derived from the HRIS
 * when editing the base, the composed base when editing a plan.
 */
export function applySquadOp(model, directory, layer, operation, options = {}) {
    // Every message that describes an override ends with this, so a toast always
    // says which of the two things just happened.
    const where = options.proposing ? ' in this plan' : '';
    switch (operation.op) {
        case 'addSquad': {
            const name = operation.name.trim();
            if (name.length === 0)
                throw new OpError('A team needs a name.');
            const existing = effectiveSquads(directory, layer).squads;
            if (existing.some((squad) => squad.name.toLowerCase() === name.toLowerCase())) {
                throw new OpError(`There is already a team called “${name}”.`);
            }
            const wanted = operation.id ?? toSquadId(name);
            const id = existing.some((squad) => squad.id === wanted) ? `${wanted}-${randomId()}` : wanted;
            const parentId = operation.parentId ?? null;
            if (parentId && !existing.some((squad) => squad.id === parentId)) {
                throw new OpError(`No team with id "${parentId}" to put it inside.`);
            }
            const squad = {
                id,
                name,
                description: operation.description?.trim() ?? '',
                members: [],
                parentId,
                color: null,
            };
            return {
                layer: { ...layer, newSquads: [...layer.newSquads, squad] },
                squadId: id,
                message: options.proposing ? `Proposed a new team “${name}”.` : `Created the team “${name}”.`,
            };
        }
        case 'renameSquad': {
            const squad = effectiveSquad(directory, layer, operation.squadId);
            const name = operation.name.trim();
            if (name.length === 0)
                throw new OpError('A team needs a name.');
            const clash = effectiveSquads(directory, layer).squads.find((candidate) => candidate.id !== squad.id && candidate.name.toLowerCase() === name.toLowerCase());
            if (clash)
                throw new OpError(`There is already a team called “${name}”.`);
            const base = baseSquad(directory, layer, squad.id);
            return {
                layer: setSquadEdit(layer, squad.id, (edit) => {
                    if (name === base?.name)
                        delete edit.name;
                    else
                        edit.name = name;
                    return edit;
                }),
                squadId: squad.id,
                message: `“${squad.name}” renamed to “${name}”${where}.`,
            };
        }
        case 'describeSquad': {
            const squad = effectiveSquad(directory, layer, operation.squadId);
            const description = operation.description.trim();
            const base = baseSquad(directory, layer, squad.id);
            return {
                layer: setSquadEdit(layer, squad.id, (edit) => {
                    if (description === (base?.description ?? ''))
                        delete edit.description;
                    else
                        edit.description = description;
                    return edit;
                }),
                squadId: squad.id,
                message: description
                    ? `Described “${squad.name}”${where}.`
                    : `Cleared the description of “${squad.name}”${where}.`,
            };
        }
        case 'setSquadParent': {
            const squad = effectiveSquad(directory, layer, operation.squadId);
            const parentId = operation.parentId ?? null;
            if (parentId) {
                const parent = effectiveSquad(directory, layer, parentId);
                // A team inside itself has no meaning and hangs anything that walks the
                // chain, so this is checked here rather than left to render badly.
                const ancestors = squadChain(effectiveSquads(directory, layer), parent.id);
                if (parent.id === squad.id || ancestors.some((step) => step.id === squad.id)) {
                    throw new OpError(`“${parent.name}” is already inside “${squad.name}”.`);
                }
            }
            const base = baseSquad(directory, layer, squad.id);
            return {
                layer: setSquadEdit(layer, squad.id, (edit) => {
                    if (parentId === (base?.parentId ?? null))
                        delete edit.parentId;
                    else
                        edit.parentId = parentId;
                    return edit;
                }),
                squadId: squad.id,
                message: parentId
                    ? `“${squad.name}” sits inside “${effectiveSquad(directory, layer, parentId).name}”${where}.`
                    : `“${squad.name}” is a top-level team${where}.`,
            };
        }
        case 'setSquadColor': {
            const squad = effectiveSquad(directory, layer, operation.squadId);
            const color = operation.color?.trim() || null;
            const base = baseSquad(directory, layer, squad.id);
            return {
                layer: setSquadEdit(layer, squad.id, (edit) => {
                    if (color === (base?.color ?? null))
                        delete edit.color;
                    else
                        edit.color = color;
                    return edit;
                }),
                squadId: squad.id,
                message: color ? `“${squad.name}” is ${color}.` : `“${squad.name}” uses its automatic colour again.`,
            };
        }
        /*
         * One operation for both, because "this team is gone" is one idea: a team
         * the layer invented is simply removed, a team from beneath is marked as
         * stood down and shows up as a change to revert.
         */
        case 'disbandSquad': {
            const squad = effectiveSquad(directory, layer, operation.squadId);
            const isNew = layer.newSquads.some((candidate) => candidate.id === squad.id);
            if (isNew) {
                const squadEdits = { ...layer.squadEdits };
                delete squadEdits[squad.id];
                return {
                    layer: {
                        ...layer,
                        newSquads: layer.newSquads.filter((candidate) => candidate.id !== squad.id),
                        squadEdits,
                    },
                    message: options.proposing
                        ? `Dropped the proposed team “${squad.name}”.`
                        : `Deleted the team “${squad.name}”.`,
                };
            }
            return {
                layer: { ...layer, disbandedSquads: [...layer.disbandedSquads, squad.id] },
                squadId: squad.id,
                message: options.proposing
                    ? `“${squad.name}” stood down in this plan.`
                    : `“${squad.name}” hidden — the HRIS still reports it.`,
            };
        }
        case 'setSquadMember': {
            const squad = effectiveSquad(directory, layer, operation.squadId);
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            const base = baseSquad(directory, layer, squad.id);
            const baseRole = base?.members.find((member) => member.personId === person.id)?.role ?? null;
            const was = squad.members.find((member) => member.personId === person.id)?.role ?? null;
            return {
                layer: setSquadEdit(layer, squad.id, (edit) => {
                    const members = { ...(edit.members ?? {}) };
                    if (operation.role === baseRole)
                        delete members[person.id];
                    else
                        members[person.id] = operation.role;
                    return { ...edit, members };
                }),
                personId: person.id,
                squadId: squad.id,
                message: was
                    ? `${person.name} is ${operation.role} on “${squad.name}”${where}.`
                    : `${person.name} joins “${squad.name}” as ${operation.role}${where}.`,
            };
        }
        case 'removeSquadMember': {
            const squad = effectiveSquad(directory, layer, operation.squadId);
            const person = model.byId.get(operation.personId);
            if (!person)
                throw new OpError(`No person with id "${operation.personId}".`);
            if (!squad.members.some((member) => member.personId === person.id)) {
                throw new OpError(`${person.name} is not on “${squad.name}”.`);
            }
            const base = baseSquad(directory, layer, squad.id);
            const onBase = base?.members.some((member) => member.personId === person.id) ?? false;
            return {
                layer: setSquadEdit(layer, squad.id, (edit) => {
                    const members = { ...(edit.members ?? {}) };
                    // Not on the team to begin with: the override is what put them there,
                    // so deleting it is the honest undo.
                    if (onBase)
                        members[person.id] = null;
                    else
                        delete members[person.id];
                    return { ...edit, members };
                }),
                personId: person.id,
                squadId: squad.id,
                message: `${person.name} leaves “${squad.name}”${where}.`,
            };
        }
        default: {
            const exhaustive = operation;
            throw new OpError(`Unknown team operation "${JSON.stringify(exhaustive)}".`);
        }
    }
}
/**
 * Undo one team change. Ids carry everything needed: the squad, and for a
 * membership change the person, so a revert never has to guess.
 */
export function revertSquadChange(model, directory, layer, changeId, options = {}) {
    const [kind, rest] = splitChangeId(changeId);
    const [squadId, personId] = kind === 'squad-member' ? splitChangeId(rest) : [rest, undefined];
    const squad = baseSquad(directory, layer, squadId);
    const name = squad?.name ?? squadId;
    switch (kind) {
        case 'squad-add':
            return applySquadOp(model, directory, layer, { op: 'disbandSquad', squadId }, options);
        case 'squad-disband':
            return {
                layer: { ...layer, disbandedSquads: layer.disbandedSquads.filter((id) => id !== squadId) },
                squadId,
                message: `“${name}” stands as it is.`,
            };
        case 'squad-rename':
            return {
                layer: setSquadEdit(layer, squadId, (edit) => {
                    delete edit.name;
                    return edit;
                }),
                squadId,
                message: `Reverted the name of “${name}”.`,
            };
        case 'squad-parent':
            return {
                layer: setSquadEdit(layer, squadId, (edit) => {
                    delete edit.parentId;
                    return edit;
                }),
                squadId,
                message: `“${name}” sits where it did.`,
            };
        case 'squad-member': {
            if (!personId)
                throw new OpError(`Malformed change id "${changeId}".`);
            return {
                layer: setSquadEdit(layer, squadId, (edit) => {
                    const members = { ...(edit.members ?? {}) };
                    delete members[personId];
                    return { ...edit, members };
                }),
                personId,
                squadId,
                message: `Reverted a membership change on “${name}”.`,
            };
        }
        default:
            throw new OpError(`Unknown change id "${changeId}".`);
    }
}
/**
 * What a layer proposes for the teams, against whatever sits beneath it.
 *
 * A team the layer invented is one `squad-add`, not one change per founding
 * member — the members are part of proposing the team. Membership changes to a
 * team that already exists are listed one by one, because each is a decision
 * somebody might want to revert on its own.
 */
export function diffSquads(model, layer, directory) {
    const changes = [];
    const effective = effectiveSquads(directory, layer);
    const nameOf = (personId) => model.byId.get(personId)?.name ?? personId;
    for (const squad of effective.squads) {
        const base = directory.squads.find((candidate) => candidate.id === squad.id);
        if (!base) {
            changes.push({
                id: `squad-add:${squad.id}`,
                kind: 'squad-add',
                squadId: squad.id,
                squadName: squad.name,
                memberCount: squad.members.length,
            });
            continue;
        }
        if ((squad.parentId ?? null) !== (base.parentId ?? null)) {
            const nameOfSquad = (id) => id ? (effective.squads.find((candidate) => candidate.id === id)?.name ?? id) : null;
            changes.push({
                id: `squad-parent:${squad.id}`,
                kind: 'squad-parent',
                squadId: squad.id,
                squadName: squad.name,
                fromParentName: nameOfSquad(base.parentId),
                toParentName: nameOfSquad(squad.parentId),
            });
        }
        if (squad.name !== base.name) {
            changes.push({
                id: `squad-rename:${squad.id}`,
                kind: 'squad-rename',
                squadId: squad.id,
                squadName: squad.name,
                fromName: base.name,
                toName: squad.name,
            });
        }
        const before = new Map(base.members.map((member) => [member.personId, member.role]));
        const after = new Map(squad.members.map((member) => [member.personId, member.role]));
        for (const personId of new Set([...before.keys(), ...after.keys()])) {
            const fromRole = before.get(personId) ?? null;
            const toRole = after.get(personId) ?? null;
            if (fromRole === toRole)
                continue;
            changes.push({
                id: `squad-member:${squad.id}:${personId}`,
                kind: 'squad-member',
                squadId: squad.id,
                squadName: squad.name,
                personId,
                personName: nameOf(personId),
                fromRole,
                toRole,
            });
        }
    }
    for (const squadId of layer.disbandedSquads) {
        const base = directory.squads.find((candidate) => candidate.id === squadId);
        if (!base)
            continue;
        changes.push({
            id: `squad-disband:${squadId}`,
            kind: 'squad-disband',
            squadId,
            squadName: base.name,
            memberCount: base.members.length,
        });
    }
    return changes;
}
//# sourceMappingURL=squads.js.map